import { FC, useState } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, Row, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { fetchDoctors } from 'services/api/doctors';
import { INVOICE_SERVICE_TYPE_OPTIONS } from 'constants/invoicing';
import { InvoicingProduct } from 'types/api/invoicing';

import ProductModal from '../../../Products/components/ProductModal';
import AsyncUserSelect, { PickerUser } from '../AsyncUserSelect';

// Doctor search spans all organizations (see CreateInvoice patient search).
const ALL_ORGS = { headers: { organization: null as unknown as string } };

const fetchDoctorOptions = (search: string): Promise<PickerUser[]> =>
  fetchDoctors({ page: 1, limit: 20, search }, ALL_ORGS).then(
    res => res.results
  );

interface IProps {
  products: InvoicingProduct[];
  onChanged: () => void;
  // Refetch the catalog and return the fresh list (for inline product create).
  reloadProducts: () => Promise<InvoicingProduct[]>;
}

const LineItems: FC<IProps> = ({ products, onChanged, reloadProducts }) => {
  const form = Form.useFormInstance();

  // Which line opened the "New product" modal (null = closed).
  const [addForLine, setAddForLine] = useState<number | null>(null);

  const productOptions = products.map(product => ({
    value: product.publicId,
    label: `${product.nameEn} · ${product.serviceType}`
  }));

  // Prefill a line from a product (service type, descriptions, unit, tax).
  const applyProductPrefill = (name: number, product: InvoicingProduct) => {
    form.setFieldValue(['lines', name, 'lineType'], product.serviceType);
    form.setFieldValue(['lines', name, 'descriptionEn'], product.nameEn);
    form.setFieldValue(['lines', name, 'descriptionDe'], product.nameDe);
    form.setFieldValue(
      ['lines', name, 'unitAmount'],
      Number(product.defaultCost)
    );
    form.setFieldValue(
      ['lines', name, 'taxRate'],
      Number(product.defaultTaxRate)
    );
  };

  const onProductChange = (name: number, productId?: string) => {
    if (!productId) {
      onChanged();

      return;
    }

    const product = products.find(item => item.publicId === productId);

    if (product) {
      applyProductPrefill(name, product);
    }

    onChanged();
  };

  // After creating a product inline: refetch, find the new one (not in the
  // previous list, else the newest id), select it into the line, and prefill.
  const handleProductCreated = async () => {
    const line = addForLine;

    if (line == null) {
      return;
    }

    const before = new Set(products.map(item => item.publicId));

    try {
      const list = await reloadProducts();
      const created =
        list.find(item => !before.has(item.publicId)) ??
        list.reduce<InvoicingProduct | null>(
          (newest, item) =>
            !newest || Number(item.id) > Number(newest.id) ? item : newest,
          null
        );

      if (created) {
        form.setFieldValue(['lines', line, 'productId'], created.publicId);
        applyProductPrefill(line, created);
        onChanged();
      }
    } catch {
      // reload failed — the catalog just won't refresh; user can retry.
    }
  };

  return (
    <Form.List name="lines">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name }) => (
            <Card
              key={key}
              size="small"
              style={{ marginBottom: 12 }}
              extra={
                fields.length > 1 && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      remove(name);
                      onChanged();
                    }}
                  />
                )
              }
              title={`Line ${name + 1}`}
            >
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Product (prefill)">
                    <Row gutter={8} wrap={false}>
                      <Col flex="auto">
                        <Form.Item name={[name, 'productId']} noStyle>
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="From catalog"
                            options={productOptions}
                            onChange={value => onProductChange(name, value)}
                          />
                        </Form.Item>
                      </Col>
                      <Col flex="none">
                        <Button onClick={() => setAddForLine(name)}>
                          + Add
                        </Button>
                      </Col>
                    </Row>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={[name, 'lineType']} label="Service type">
                    <Select
                      allowClear
                      placeholder="Falls back to product"
                      options={INVOICE_SERVICE_TYPE_OPTIONS}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={[name, 'descriptionEn']}
                    label="Description (EN)"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={[name, 'descriptionDe']}
                    label="Description (DE)"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={[name, 'doctor']} label="Doctor">
                    <AsyncUserSelect
                      fetcher={fetchDoctorOptions}
                      showEmail={false}
                      allowClear
                      placeholder="Search a doctor (optional)"
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    name={[name, 'quantity']}
                    label="Qty"
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    name={[name, 'unitAmount']}
                    label="Unit amount"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name={[name, 'taxRate']} label="Tax %">
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={[name, 'discountType']}
                    label="Discount type"
                  >
                    <Select
                      allowClear
                      placeholder="No discount"
                      options={[
                        { value: 'amount', label: 'Amount' },
                        { value: 'percent', label: 'Percent (%)' }
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={[name, 'discountValue']}
                    label="Discount value"
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() =>
              add({ quantity: 1, unitAmount: 0, discountType: 'percent' })
            }
          >
            Add line item
          </Button>

          <ProductModal
            open={addForLine != null}
            product={null}
            onClose={() => setAddForLine(null)}
            onSuccess={handleProductCreated}
          />
        </>
      )}
    </Form.List>
  );
};

export default LineItems;
